import React, { useState } from "react";
import { Search } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { asText, CardShell, EmptyRow, formatCompact, formatCurrency, formatRoas, LoadingRow, StatusBadge, unwrapValue, useCxoResource } from "../shared";
import { getGenderIconMeta, GENDER_COLORS as GENDER_ICON_COLORS } from "../device-gender-icons";

// Generic Audience tab — identical shape at every level (Campaign/Ad set/Ad),
// confirmed by the doc's own repeated description: "by_gender[].impressions_share_pct
// gives the 52%/46%/2% split; by_age, cells, top_segment and highlights feed
// the age views." `items` (campaign-level only) is the Audience performance
// table; ad set/ad level audience calls don't return a table, just the
// gender/age breakdown, so that section only renders when `items` exists.

const DONUT_COLORS = ["#4f46e5", "#60a5fa", "#f59e0b", "#a855f7", "#10b981", "#f43f5e"];
// Same colours as the Male/Female icons themselves (device-gender-icons.tsx).
const GENDER_COLORS: Record<string, string> = { ...GENDER_ICON_COLORS, unknown: "#a1a1aa" };

// Swaps recharts' own auto-generated color-swatch legend for the real
// male/female icons — `payload[].value` is each <Bar>'s own dataKey, which
// here is the raw gender string (see the BarChart below).
const GenderLegend: React.FC<any> = ({ payload }) => (
  <div className="flex items-center justify-center gap-4 mt-1">
    {(payload || []).map((entry: any, i: number) => {
      const meta = getGenderIconMeta(entry.value);
      return (
        <div key={i} className="flex items-center gap-1.5 text-[11px]">
          {meta.Icon ? <meta.Icon size={12} /> : <span className="w-2 h-2 rounded-full shrink-0" style={{ background: entry.color }} />}
          <span className="text-gray-600 dark:text-gray-300">{meta.label}</span>
        </div>
      );
    })}
  </div>
);

const AudienceTab: React.FC<{ basePath: string; entityId: string; baseParams: Record<string, any> }> = ({ basePath, entityId, baseParams }) => {
  const [search, setSearch] = useState("");
  const { data, loading } = useCxoResource<any>(basePath, `${entityId}/audience`, { ...baseParams, metric: "spend" }, !!entityId);

  if (loading && !data) return <LoadingRow label="Loading audience…" />;
  if (!data) return <EmptyRow label="No audience data for this period yet." />;

  const insights = data.insights || {};
  const items: any[] = Array.isArray(data.items) ? data.items : [];
  const filteredItems = search ? items.filter((it) => asText(it.name ?? it.label).toLowerCase().includes(search.toLowerCase())) : items;
  const mix = data.mix || {};
  const mixItems: any[] = Array.isArray(mix.items) ? mix.items : Array.isArray(data.mix) ? data.mix : [];
  const byGender: any[] = Array.isArray(data.by_gender) ? data.by_gender : [];
  const byAge: any[] = Array.isArray(data.by_age) ? data.by_age : [];
  const ageGenderChart = byAge.map((a) => {
    const row: Record<string, any> = { bucket: asText(a.age ?? a.bucket ?? a.range) };
    byGender.forEach((g) => {
      // Best-effort: per-cell split isn't spelled out beyond `cells`, so this
      // falls back to the age bucket's own reach split by the overall gender
      // share when a dedicated per-cell value isn't present.
      const cell = Array.isArray(data.cells) ? data.cells.find((c: any) => asText(c.age ?? c.bucket) === row.bucket && asText(c.gender) === asText(g.gender)) : null;
      row[asText(g.gender)] = unwrapValue(cell?.reach ?? cell?.value) ?? null;
    });
    return row;
  });

  return (
    <div className="space-y-5">
      {(insights.best_performing || insights.highest_reach || insights.lowest_cpa || insights.custom_audience_share) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            // Confirmed live: the real key is `best_performing`, not
            // `best_performing_audience` — this card was always empty since
            // it read a field that doesn't exist.
            { label: "Best performing audience", raw: insights.best_performing, value: (v: any) => asText(v?.name, "N/A"), sub: (v: any) => (v?.roas ? `${formatRoas(v.roas)} ROAS` : null) },
            { label: "Highest reach", raw: insights.highest_reach, value: (v: any) => asText(v?.name, "N/A"), sub: (v: any) => (v?.reach ? `${formatCompact(v.reach)} reach` : null) },
            // Confirmed live: the real field is `cost_per_result`, not `cpa`
            // — the subtitle was always blank since it read a field that
            // doesn't exist.
            { label: "Lowest CPA", raw: insights.lowest_cpa, value: (v: any) => asText(v?.name, "N/A"), sub: (v: any) => (v?.cost_per_result != null ? `${formatCurrency(v.cost_per_result)} CPA` : null) },
            // `custom_audience_share` has no name/label at all (confirmed
            // live — just `share_pct`/`spend`/`results`) — this card was
            // passing the whole object into asText(), which just stringifies
            // it to "[object Object]". The real headline value here is the
            // share percentage itself, not an audience name.
            {
              label: "Retargeting share",
              raw: insights.custom_audience_share,
              value: (v: any) => (v?.share_pct != null ? `${Number(unwrapValue(v.share_pct)).toFixed(0)}%` : "N/A"),
              sub: (v: any) => (v?.spend != null ? `${formatCurrency(v.spend)} spend` : null),
            },
          ].map((c) => (
            <div key={c.label} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">{c.label}</p>
              <p className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">{c.value(c.raw)}</p>
              {c.sub(c.raw) && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{c.sub(c.raw)}</p>}
            </div>
          ))}
        </div>
      )}

      {items.length > 0 && (
        <CardShell title="Audience performance" description="Compare performance across audience segments.">
          <div className="relative flex-1 min-w-[200px] mb-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search audiences..."
              className="w-full max-w-xs pl-8 pr-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400"
            />
          </div>
          <div className="overflow-x-auto -mx-1">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 tracking-wide">
                  <th className="pb-2 pl-1">AUDIENCE NAME</th>
                  <th className="pb-2">STATUS</th>
                  <th className="pb-2">AGE / GENDER</th>
                  <th className="pb-2">INTEREST OR SOURCE</th>
                  <th className="pb-2">SPEND</th>
                  <th className="pb-2">REACH</th>
                  <th className="pb-2">RESULTS</th>
                  <th className="pb-2">COST PER RESULT</th>
                  <th className="pb-2 pr-1 text-right">ROAS</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((it, i) => (
                  <tr key={it.adset_id || i} className="border-t border-gray-50 dark:border-gray-800">
                    <td className="py-2.5 pl-1 font-medium text-indigo-600 dark:text-indigo-400 whitespace-nowrap">{asText(it.name)}</td>
                    <td className="py-2.5">
                      <StatusBadge status={it.status ?? it.learning_stage} />
                    </td>
                    <td className="py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {it.age_min != null ? `${it.age_min}-${it.age_max ?? "+"}` : ""} {asText(it.genders)}
                    </td>
                    <td className="py-2.5 text-gray-600 dark:text-gray-300 max-w-[220px] truncate">{asText(it.interests ?? it.custom_audiences ?? it.source)}</td>
                    <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCurrency(unwrapValue(it.spend ?? it.metrics?.spend), "₦0.00")}</td>
                    <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCompact(unwrapValue(it.reach ?? it.metrics?.reach), "0")}</td>
                    <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCompact(unwrapValue(it.results ?? it.metrics?.results), "0")}</td>
                    <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCurrency(unwrapValue(it.cost_per_result ?? it.metrics?.cost_per_result), "₦0.00")}</td>
                    <td className="py-2.5 pr-1 text-right font-semibold text-emerald-600 dark:text-emerald-400">{formatRoas(unwrapValue(it.roas ?? it.metrics?.roas), "N/A")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardShell>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {mixItems.length > 0 && (
          <CardShell title="Audience mix by segment" description="Share of total spend and results by audience segment.">
            <div className="flex items-center gap-4">
              <div className="h-48 w-48 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={mixItems} dataKey={(d: any) => unwrapValue(d.value ?? d.spend) || 0} nameKey={(d: any) => asText(d.name ?? d.label)} innerRadius={48} outerRadius={72} paddingAngle={2}>
                      {mixItems.map((_, i) => (
                        <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5 text-xs flex-1 min-w-0">
                {mixItems.map((m, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                    <span className="text-gray-600 dark:text-gray-300 truncate flex-1">{asText(m.name ?? m.label)}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100 shrink-0">{formatCurrency(unwrapValue(m.value ?? m.spend))}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardShell>
        )}

        {(byAge.length > 0 || byGender.length > 0) && (
          <CardShell title="Audience age & gender split" description="Distribution of reach by age group and gender.">
            {ageGenderChart.length > 0 ? (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ageGenderChart} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Legend content={<GenderLegend />} />
                    {byGender.map((g) => (
                      <Bar key={asText(g.gender)} dataKey={asText(g.gender)} stackId="a" fill={GENDER_COLORS[asText(g.gender).toLowerCase()] || "#a1a1aa"} radius={[0, 0, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="space-y-2">
                {byGender.map((g, i) => {
                  const meta = getGenderIconMeta(asText(g.gender));
                  return (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
                        {meta.Icon ? <meta.Icon size={13} /> : <span className="w-2 h-2 rounded-full shrink-0 bg-gray-300" />}
                        {meta.label}
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{(() => { const v = unwrapValue(g.impressions_share_pct); return v != null ? `${Number(v).toFixed(0)}%` : "0%"; })()}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardShell>
        )}
      </div>
    </div>
  );
};

export default AudienceTab;
